import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme as useMuiTheme,
  SwipeableDrawer
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as ExitToAppIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  ChevronLeft as ChevronLeftIcon,
  Group as GroupIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// Adjust drawer width for better tablet experience
const drawerWidth = 260;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open?: boolean }>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)',
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const MainLayout: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  
  // Define breakpoints for different device sizes
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
  const isSmallTablet = useMediaQuery('(max-width:800px)');
  
  // Close drawer on mobile and tablet by default
  useEffect(() => {
    if (isMobile || isSmallTablet) {
      setOpen(false);
    }
  }, [isMobile, isSmallTablet]);

  // Handle orientation changes and resize events
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 800) {
        setOpen(false);
      }
    };

    const handleOrientationChange = () => {
      // Force layout recalculation after orientation change
      setTimeout(handleResize, 300);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile || isSmallTablet) {
      setOpen(false);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Visitors', icon: <PeopleIcon />, path: '/visitors' },
    { text: 'Check In', icon: <ExitToAppIcon />, path: '/check-in' },
    { text: 'Check Out', icon: <ExitToAppIcon sx={{ transform: 'rotate(180deg)' }} />, path: '/visitors' },
    { text: 'Pre-Register', icon: <PersonAddIcon />, path: '/pre-register' },
  ];

  // Admin-only menu items
  const adminMenuItems = [
    { text: 'Users', icon: <GroupIcon />, path: '/users' },
    { text: 'Role Management', icon: <SettingsIcon />, path: '/roles' },
    { text: 'Purpose Management', icon: <SettingsIcon />, path: '/purposes' },
  ];

  // Add debugging for user role
  console.log('Current user role:', user?.role);

  // Use SwipeableDrawer for mobile and tablet devices
  const drawer = (
    <>
      <DrawerHeader>
        <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
          VMS
        </Typography>
        <IconButton onClick={handleDrawerClose}>
          <ChevronLeftIcon />
        </IconButton>
      </DrawerHeader>
      
      <Divider />
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              selected={location.pathname === item.path}
              onClick={() => handleNavigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
                borderRadius: '0 20px 20px 0',
                mx: 1,
                my: 0.5,
                // Increase touch target size for tablet
                py: isSmallTablet ? 1.5 : 1,
              }}
            >
              <ListItemIcon sx={{ 
                color: location.pathname === item.path ? 'white' : 'inherit',
                // Increase icon size for better visibility on tablets
                '& .MuiSvgIcon-root': {
                  fontSize: isSmallTablet ? 24 : 20
                }
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontSize: isSmallTablet ? 16 : 'inherit'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {(user?.role?.toLowerCase() === 'admin') && (
        <>
          <Divider />
          <List>
            {adminMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton 
                  selected={location.pathname === item.path}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'white',
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                      },
                      '&:hover': {
                        backgroundColor: 'primary.main',
                      },
                    },
                    borderRadius: '0 20px 20px 0',
                    mx: 1,
                    my: 0.5,
                    // Increase touch target size for tablet
                    py: isSmallTablet ? 1.5 : 1,
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: location.pathname === item.path ? 'white' : 'inherit',
                    // Increase icon size for better visibility on tablets
                    '& .MuiSvgIcon-root': {
                      fontSize: isSmallTablet ? 24 : 20
                    }
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    primaryTypographyProps={{
                      fontSize: isSmallTablet ? 16 : 'inherit'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      <Divider />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            selected={location.pathname === '/settings'}
            onClick={() => handleNavigate('/settings')}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              },
              borderRadius: '0 20px 20px 0',
              mx: 1,
              my: 0.5,
              // Increase touch target size for tablet
              py: isSmallTablet ? 1.5 : 1,
            }}
          >
            <ListItemIcon sx={{ 
              // Increase icon size for better visibility on tablets
              '& .MuiSvgIcon-root': {
                fontSize: isSmallTablet ? 24 : 20
              }
            }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Settings" 
              primaryTypographyProps={{
                fontSize: isSmallTablet ? 16 : 'inherit'
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBarStyled position="fixed" open={open && !isMobile && !isSmallTablet}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ 
              mr: 2, 
              ...(open && { display: 'none' }),
              // Increase touch target size for tablet
              padding: isSmallTablet ? 1.2 : 1
            }}
          >
            <MenuIcon sx={{ fontSize: isSmallTablet ? 28 : 24 }} />
          </IconButton>
          <Typography 
            variant={isSmallTablet ? "h6" : "h6"} 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              // Make title responsive for tablet
              fontSize: isSmallTablet ? '1.1rem' : 'inherit',
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Visitor Management System
          </Typography>
          
          {/* Show abbreviated title on very small screens */}
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              display: { xs: 'block', sm: 'none' }
            }}
          >
            VMS
          </Typography>
          
          <IconButton 
            color="inherit" 
            onClick={toggleMode}
            sx={{ 
              // Increase touch target size for tablet
              padding: isSmallTablet ? 1.2 : 1
            }}
          >
            {mode === 'light' ? 
              <DarkModeIcon sx={{ fontSize: isSmallTablet ? 26 : 24 }} /> : 
              <LightModeIcon sx={{ fontSize: isSmallTablet ? 26 : 24 }} />
            }
          </IconButton>
          
          <IconButton 
            color="inherit" 
            onClick={handleNotificationsOpen}
            sx={{ 
              // Increase touch target size for tablet
              padding: isSmallTablet ? 1.2 : 1
            }}
          >
            <Badge badgeContent={3} color="error">
              <NotificationsIcon sx={{ fontSize: isSmallTablet ? 26 : 24 }} />
            </Badge>
          </IconButton>
          
          <IconButton
            edge="end"
            aria-label="account of current user"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
            sx={{ 
              // Increase touch target size for tablet
              padding: isSmallTablet ? 1.2 : 1
            }}
          >
            <Avatar sx={{ 
              width: isSmallTablet ? 38 : 32, 
              height: isSmallTablet ? 38 : 32, 
              bgcolor: 'primary.dark' 
            }}>
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBarStyled>
      
      {/* Use SwipeableDrawer for mobile and tablet devices */}
      {(isMobile || isSmallTablet) ? (
        <SwipeableDrawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          anchor="left"
          open={open}
          onClose={handleDrawerClose}
          onOpen={handleDrawerOpen}
          disableBackdropTransition={false}
          disableDiscovery={false}
          disableSwipeToOpen={false}
          swipeAreaWidth={30}
          hysteresis={0.3}
          minFlingVelocity={500}
        >
          {drawer}
        </SwipeableDrawer>
      ) : (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          {drawer}
        </Drawer>
      )}
      
      <Main open={open && !isMobile && !isSmallTablet} sx={{ 
        // Adjust padding for tablet
        padding: { xs: 1.5, sm: 2, md: 3 },
        minHeight: '100vh',
        overflowX: 'hidden'
      }}>
        <DrawerHeader />
        <Outlet />
      </Main>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { 
            minWidth: isSmallTablet ? 240 : 200,
            '& .MuiMenuItem-root': {
              // Increase touch target size for tablet
              padding: isSmallTablet ? '12px 16px' : '8px 16px',
            }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">{user?.name}</Typography>
          <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { handleProfileMenuClose(); handleNavigate('/profile'); }}>
          <ListItemIcon>
            <PersonIcon fontSize={isSmallTablet ? "medium" : "small"} />
          </ListItemIcon>
          <ListItemText primary="Profile" primaryTypographyProps={{
            fontSize: isSmallTablet ? 16 : 'inherit'
          }} />
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); handleNavigate('/settings'); }}>
          <ListItemIcon>
            <SettingsIcon fontSize={isSmallTablet ? "medium" : "small"} />
          </ListItemIcon>
          <ListItemText primary="Settings" primaryTypographyProps={{
            fontSize: isSmallTablet ? 16 : 'inherit'
          }} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleProfileMenuClose(); handleLogout(); }}>
          <ListItemIcon>
            <ExitToAppIcon fontSize={isSmallTablet ? "medium" : "small"} />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{
            fontSize: isSmallTablet ? 16 : 'inherit'
          }} />
        </MenuItem>
      </Menu>
      
      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={Boolean(notificationsAnchorEl)}
        onClose={handleNotificationsClose}
        PaperProps={{
          elevation: 3,
          sx: { 
            minWidth: isSmallTablet ? 350 : 300, 
            maxWidth: isSmallTablet ? 400 : 350,
            '& .MuiMenuItem-root': {
              // Increase touch target size for tablet
              padding: isSmallTablet ? '12px 16px' : '8px 16px',
            }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleNotificationsClose}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" fontWeight="medium">New visitor check-in</Typography>
            <Typography variant="body2" color="text.secondary">John Doe has checked in to meet you</Typography>
            <Typography variant="caption" color="text.secondary">2 minutes ago</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleNotificationsClose}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" fontWeight="medium">Visitor check-out</Typography>
            <Typography variant="body2" color="text.secondary">Jane Smith has checked out</Typography>
            <Typography variant="caption" color="text.secondary">1 hour ago</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleNotificationsClose}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body1" fontWeight="medium">Pre-registration</Typography>
            <Typography variant="body2" color="text.secondary">New visitor pre-registered for tomorrow</Typography>
            <Typography variant="caption" color="text.secondary">3 hours ago</Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleNotificationsClose}>
          <Typography color="primary" sx={{ width: '100%', textAlign: 'center' }}>
            View all notifications
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MainLayout;
